// @ts-nocheck -- inherited from lift-teams; pre-existing type issues outside this port's scope
import { BrandThreshold } from '../types';

import { parseEventValue } from './eventValueUtils';

/**
 * Utility functions for filtering thresholds based on pixel and event selections
 * Extracted from BrandThresholdTable to reduce complexity
 */

/**
 * Check if a threshold matches the pixel filter
 * @param threshold - The threshold to check
 * @param selectedPixels - Array of selected pixel IDs
 * @returns true if threshold matches filter or should be shown
 */
export const matchesPixelFilter = (
  threshold: BrandThreshold,
  selectedPixels: string[]
): boolean => {
  // No filter applied - show all
  if (selectedPixels.length === 0) {
    return true;
  }

  const hasPixel = threshold.pixelId && threshold.pixelId.trim() !== '';

  // Threshold has no pixel - always show (allows adding new rows)
  if (!hasPixel) {
    return true;
  }

  // Check if threshold's pixel matches selected pixels
  return selectedPixels.includes(threshold.pixelId!);
};

/**
 * Check if a threshold matches the event filter
 * @param threshold - The threshold to check
 * @param selectedEvents - Array of selected event values
 * @returns true if threshold matches filter or should be shown
 */
export const matchesEventFilter = (
  threshold: BrandThreshold,
  selectedEvents: string[]
): boolean => {
  // No filter applied - show all
  if (selectedEvents.length === 0) {
    return true;
  }

  const hasEvent = threshold.eventName && threshold.eventName.trim() !== '';

  // Threshold has no event - always show (allows adding new rows)
  if (!hasEvent) {
    return true;
  }

  // Threshold has event but no eventId yet - show it
  if (!threshold.eventId) {
    return true;
  }

  // Check if the threshold's event matches any selected event
  return selectedEvents.some(selectedEventValue => {
    const parsed = parseEventValue(selectedEventValue);
    if (!parsed) return false;
    // Match by event ID and name (ignore pixel in comparison for flexibility)
    return parsed.eventId === threshold.eventId && parsed.eventName === threshold.eventName;
  });
};

/**
 * Check if a threshold has been manually edited
 * Manually edited thresholds should always be shown regardless of filters
 * @param threshold - The threshold to check
 * @returns true if threshold has been manually edited
 */
export const isManuallyEdited = (threshold: BrandThreshold): boolean => {
  const hasPixel = threshold.pixelId && threshold.pixelId.trim() !== '';
  const hasEvent = threshold.eventId && threshold.eventName;
  return hasPixel || hasEvent;
};

/**
 * Filter thresholds based on pixel and event selections
 * @param thresholds - Array of thresholds to filter
 * @param selectedPixels - Array of selected pixel IDs
 * @param selectedEvents - Array of selected event values
 * @returns Filtered array of thresholds
 */
export const filterThresholds = (
  thresholds: BrandThreshold[],
  selectedPixels: string[],
  selectedEvents: string[]
): BrandThreshold[] => {
  return thresholds.filter(threshold => {
    // Manually edited rows should always be visible
    if (isManuallyEdited(threshold)) {
      return true;
    }

    // Filter by pixels
    if (!matchesPixelFilter(threshold, selectedPixels)) {
      return false;
    }

    // Filter by events
    if (!matchesEventFilter(threshold, selectedEvents)) {
      return false;
    }

    return true;
  });
};

/**
 * Get border classes based on connection state
 * Simplifies border calculation logic
 * @param hasConnectionFromPrev - Whether previous row connects to this one
 * @param hasConnectionToNext - Whether this row connects to next one
 * @returns Object with border classes for different columns
 */
export const getBorderClasses = (hasConnectionFromPrev: boolean, hasConnectionToNext: boolean) => {
  // Determine border classes for different columns
  const firstColBorderClass = `border-l border-gray-300 ${
    hasConnectionFromPrev ? '' : 'border-t'
  } ${hasConnectionToNext ? '' : 'border-b'}`;

  const middleColBorderClass = `border-gray-300 ${hasConnectionFromPrev ? '' : 'border-t'} ${
    hasConnectionToNext ? '' : 'border-b'
  }`;

  const lastColBorderClass = `border-r border-gray-300 ${hasConnectionFromPrev ? '' : 'border-t'} ${
    hasConnectionToNext ? '' : 'border-b'
  }`;

  return {
    firstColBorderClass,
    middleColBorderClass,
    lastColBorderClass,
  };
};

/**
 * Check if a threshold row has a connection to the next row
 * @param threshold - The threshold to check
 * @param isLastRow - Whether this is the last row
 * @returns true if row connects to next row
 */
export const hasConnectionToNext = (threshold: BrandThreshold, isLastRow: boolean): boolean => {
  return !isLastRow && !!threshold.logicalOperator && threshold.logicalOperator !== 'NONE';
};

/**
 * Check if a threshold row has a connection from the previous row
 * @param prevThreshold - The previous threshold (or null if first row)
 * @returns true if previous row connects to this row
 */
export const hasConnectionFromPrev = (prevThreshold: BrandThreshold | null): boolean => {
  return (
    !!prevThreshold && !!prevThreshold.logicalOperator && prevThreshold.logicalOperator !== 'NONE'
  );
};